// #1031: My Trades' Polymarket US section — the trader's own US fills and positions,
// beside the K/P tables rather than inside them: the shared TradeCardVM rows carry
// numbers, and US quantities, prices and USD amounts must stay exact decimal text.

import { VenueChip } from '../../components/atoms/VenueChip';
import { SideBadge } from '../../components/cards/cardKit';
import { maskPnl, maskPnlColor } from '../../lib/pnlMask';
import { US_PROVISIONAL_TITLE, usDollarsToCents, usPriceDashTitle } from '../../lib/fills/usPrice';
import type { UsMyTradeFill, UsMyTradePosition, UsPositionEvidence } from '../../lib/polymarketUs/myTradesClient';
import { Th, formatDateOnly } from './tableKit';

/** Exact signed USD text, e.g. '-0.0125' → '-$0.0125'; null is unknown, shown as a dash. */
function usd(value: string | null): string {
  if (value === null) return '—';
  return value.startsWith('-') ? `-$${value.slice(1)}` : `$${value}`;
}

const EVIDENCE_TITLE: Record<UsPositionEvidence, string> = {
  complete: '',
  fee_incomplete: 'a fee is missing from the venue evidence',
  orphan_sell: 'a sale exceeds the recorded holding',
  cost_basis_unknown: 'the cost basis cannot be established from the recorded fills',
  payout_unverified: 'P&L pending verified payout',
};

function UsdCell({ value, hidePnl, signed = false, title }: { value: string | null; hidePnl: boolean; signed?: boolean; title?: string }) {
  const tone = !signed || value === null ? 'text-muted-foreground'
    : value.startsWith('-') ? 'text-red-400' : /^0(?:\.0+)?$/.test(value) ? 'text-foreground' : 'text-green-400';
  const text = signed && value !== null && !value.startsWith('-') && !/^0(?:\.0+)?$/.test(value) ? `+${usd(value)}` : usd(value);
  return (
    <td className={`whitespace-nowrap px-3 py-1.5 text-right font-mono ${maskPnlColor(hidePnl, tone)}`} title={value === null ? title : undefined}>
      {maskPnl(hidePnl, text)}
    </td>
  );
}

function UsPrice({ fill }: { fill: UsMyTradeFill }) {
  if (fill.price === null) return <span title={usPriceDashTitle(fill.priceBasis)}>—</span>;
  return (
    <>
      {usDollarsToCents(fill.price)}¢
      {fill.priceBasis === 'provisional' && (
        <span className="text-muted-foreground/60" title={US_PROVISIONAL_TITLE}>*</span>
      )}
    </>
  );
}

export function UsTradesSection({
  fills,
  positions,
  notes,
  hidePnl,
}: {
  fills: UsMyTradeFill[];
  positions: UsMyTradePosition[];
  /** Withheld / pending-payout / unavailable notes, shown under the header. */
  notes: string[];
  hidePnl: boolean;
}) {
  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border" data-testid="us-trades-section">
      <div className="border-border border-b px-4 py-2.5">
        <div className="flex items-center gap-2">
          <VenueChip venue="polymarket_us" />
          <h3 className="text-foreground text-sm font-medium">Polymarket US Trades</h3>
        </div>
        {notes.length > 0 && <p className="text-muted-foreground mt-1 break-words text-[11px]">{notes.join(' · ')}</p>}
      </div>
      {positions.length > 0 && (
        <>
        <SubHeading>Positions</SubHeading>
        <div className="overflow-x-auto" role="region" aria-label="Polymarket US positions" tabIndex={0}>
        <table className="w-full min-w-max text-xs [&_th]:whitespace-nowrap">
          <SharedColumns />
          <thead className="bg-muted/30">
            <tr className="text-muted-foreground border-border border-b">
              <Th>Venue</Th>
              <Th>Status</Th>
              <Th>Market</Th>
              <Th align="center">Side</Th>
              <Th align="right">Qty</Th>
              <Th align="right">Cost</Th>
              <Th align="right">Trading P&amp;L</Th>
              <Th align="right">Fees</Th>
              <Th align="right">Settlement</Th>
              <Th align="right">Net P&amp;L</Th>
            </tr>
          </thead>
          <tbody>
            {positions.map((p) => (
              <tr key={`${p.accountId}:${p.marketSlug}:${p.outcome}`} className="border-border/40 border-b last:border-0">
                <td className="px-3 py-1.5"><VenueChip venue="polymarket_us" /></td>
                <td className="px-3 py-1.5">
                  <span className="bg-muted text-foreground rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                    {p.status}
                  </span>
                </td>
                <td className="text-foreground max-w-[320px] break-words px-3 py-1.5 font-mono text-[11px]" title={p.marketSlug}>
                  {p.marketTitle ?? p.marketSlug}
                </td>
                <td className="px-3 py-1.5 text-center"><SideBadge side={p.outcome} /></td>
                <td className="text-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">{p.netQuantity || '—'}</td>
                <UsdCell value={p.costBasisUsd} hidePnl={hidePnl} title={EVIDENCE_TITLE[p.evidence]} />
                <UsdCell value={p.tradingRealizedUsd} hidePnl={hidePnl} signed title={EVIDENCE_TITLE[p.evidence]} />
                <UsdCell value={p.realizedFeesUsd} hidePnl={hidePnl} title={EVIDENCE_TITLE[p.evidence]} />
                <UsdCell value={p.settlementPnlUsd} hidePnl={hidePnl} signed title={EVIDENCE_TITLE[p.evidence]} />
                <UsdCell value={p.totalRealizedNetUsd} hidePnl={hidePnl} signed title={EVIDENCE_TITLE[p.evidence]} />
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        </>
      )}
      {fills.length > 0 && (
        <>
        <SubHeading divided={positions.length > 0}>Fills</SubHeading>
        <div className="overflow-x-auto" role="region" aria-label="Polymarket US fills" tabIndex={0}>
        <table className="w-full min-w-max text-xs [&_th]:whitespace-nowrap">
          <SharedColumns />
          <thead className="bg-muted/30">
            <tr className="text-muted-foreground border-border border-b">
              <Th>Venue</Th>
              <Th>Filled</Th>
              <Th>Market</Th>
              <Th align="center">Side</Th>
              <Th align="center">Action</Th>
              <Th align="right">Qty</Th>
              <Th align="right">Price</Th>
              <Th align="right">Fee</Th>
            </tr>
          </thead>
          <tbody>
            {fills.map((f) => (
              <tr key={`${f.accountId}:${f.executionId}`} className="border-border/40 border-b last:border-0">
                <td className="px-3 py-1.5"><VenueChip venue="polymarket_us" /></td>
                <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5" title={f.executedAt ?? 'time unknown'}>
                  {f.executedAt === null ? '—' : formatDateOnly(Date.parse(f.executedAt))}
                </td>
                <td className="text-foreground max-w-[320px] break-words px-3 py-1.5 font-mono text-[11px]" title={f.marketSlug}>
                  {f.marketTitle ?? f.marketSlug}
                </td>
                <td className="px-3 py-1.5 text-center"><SideBadge side={f.outcome} /></td>
                <td className="px-3 py-1.5 text-center uppercase">{f.action}</td>
                <td className="text-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">{f.quantity}</td>
                <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono"><UsPrice fill={f} /></td>
                <UsdCell value={f.feeUsd} hidePnl={hidePnl} title="fee not reported" />
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        </>
      )}
      {(positions.length > 0 || fills.length > 0) && (
        <p className="text-muted-foreground border-border border-t px-3 py-1.5 text-[10px]">Scroll horizontally to view all columns when needed.</p>
      )}
    </div>
  );
}

/** The positions and fills tables share their leading identity columns (venue, status or fill
 *  date, market, side), so the market column lines up across both. The market column is a share
 *  of the table, not a fixed width, so it widens with the screen instead of leaving the number
 *  columns spread across the rest (#1111); long slugs wrap inside it. The market cells keep their
 *  max-w-[320px]: it is not their width, it caps what a long name contributes to the min-w-max
 *  table's width. A percentage column sizes the table from its content (about the name's unwrapped
 *  width ÷ 0.3), so without the cap one long slug widened the 1470 table to ~2440px, scrolling. */
function SharedColumns() {
  return (
    <colgroup>
      <col className="w-16" />
      <col className="w-28" />
      <col className="w-[30%]" />
      <col className="w-16" />
    </colgroup>
  );
}

function SubHeading({ children, divided = false }: { children: React.ReactNode; divided?: boolean }) {
  return (
    <div className={`text-muted-foreground border-border border-b px-3 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-wide${divided ? ' border-t mt-2' : ''}`}>
      {children}
    </div>
  );
}
